import { useClose } from "@headlessui/react";
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  BellIcon,
  ChatBubbleLeftRightIcon,
  UserPlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { format, isToday } from "date-fns";
import React from "react";
import { HiOutlineDocumentText } from "react-icons/hi";
import { useNavigate } from "react-router-dom";

import { Button, Paragraph } from "@fronterahealth/frontera-ui-components";

import { ApiNotificationsNotificationTypeChoices, NotificationsType } from "@api/graphql/types-and-hooks";
import { getNotificationLink } from "@providers/notificationNavigation";

interface NotificationsPanelProps {
  notifications: NotificationsType[];
  readNotification: (notification: NotificationsType) => Promise<string | undefined>;
  readAllNotifications: () => Promise<void>;
  dismissNotification: (notification: NotificationsType) => Promise<void>;
  totalUnread: number;
  onClose?: () => void;
}

export const getNotificationIcon = (notificationType: ApiNotificationsNotificationTypeChoices) => {
  switch (notificationType) {
    case ApiNotificationsNotificationTypeChoices.ExpertChatMatchAssigned:
      return <UserPlusIcon className="w-5 h-5 text-text-secondary" />;
    case ApiNotificationsNotificationTypeChoices.ExpertChatReplyNeeded:
      return <ArrowUturnLeftIcon className="w-5 h-5 text-text-secondary" />;
    case ApiNotificationsNotificationTypeChoices.ExpertChatNewMessage:
      return <ChatBubbleLeftRightIcon className="w-5 h-5 text-text-secondary" />;
    case ApiNotificationsNotificationTypeChoices.ReassessmentReminder:
      return <HiOutlineDocumentText className="w-5 h-5 text-text-secondary" />;
    case ApiNotificationsNotificationTypeChoices.ProgrammingRefreshCompleted:
      return <ArrowPathIcon className="w-5 h-5 text-text-secondary" />;
    default:
      return <BellIcon className="w-5 h-5 text-text-secondary" />;
  }
};

export const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  notifications,
  readNotification,
  readAllNotifications,
  dismissNotification,
  totalUnread,
  onClose,
}) => {
  const navigate = useNavigate();
  // Closes the desktop bell Popover. No-ops outside an overlay (the mobile panel uses onClose).
  const closeOverlay = useClose();

  const notificationOnClick = async (notification: NotificationsType) => {
    // Before the await: closing refocuses the bell, which must not pull focus back once the user has moved on.
    closeOverlay();
    const path = await readNotification(notification);

    if (onClose) {
      onClose();
    }

    if (path) {
      navigate(path);
    }
  };

  return (
    <div className="flex flex-col -m-3 h-full">
      <div className="flex flex-row p-4 border-b justify-between items-center">
        <Paragraph displayType="loud" colorType="primary" size="small">
          Notifications
        </Paragraph>
        <div className="flex flex-row gap-2 items-center justify-end">
          {totalUnread > 0 && (
            <Button
              onClick={readAllNotifications}
              appearance="text"
              buttonAction="regular"
              text="Mark all as read"
              className="-mt-5"
            />
          )}
          {onClose && (
            <button onClick={onClose}>
              <XMarkIcon className="h-5" />
            </button>
          )}
        </div>
      </div>
      {notifications.length > 0 ? (
        <div className="flex-1 overflow-y-auto">
          {notifications.map((notification) => {
            const link = getNotificationLink(notification);

            return (
              <div
                key={notification.id}
                className="flex flex-row gap-2 p-4 border-b cursor-pointer hover:bg-limestone-50 transition-colors"
                onClick={() => {
                  notificationOnClick(notification);
                }}
              >
                <div className="flex flex-col pt-0.5">{getNotificationIcon(notification.notificationType)}</div>
                <div className="flex flex-col gap-1 flex-1">
                  <Paragraph displayType="loud" colorType="primary" size="small" className="ph-no-capture">
                    {notification.title}
                  </Paragraph>
                  <Paragraph colorType="primary" size="small" className="ph-no-capture">
                    {notification.description}
                  </Paragraph>
                  {link && (
                    <Paragraph displayType="loud" size="small" className="text-dynamic-text-primary-default underline">
                      {link}
                    </Paragraph>
                  )}
                  <Paragraph size="extra-small">
                    {isToday(new Date(notification.createdDate))
                      ? format(new Date(notification.createdDate), "h:mm a")
                      : format(new Date(notification.createdDate), "MMMM do, yyyy")}
                  </Paragraph>
                </div>
                <div className="flex flex-row items-start gap-1 pt-0.5">
                  {!notification.read && <div className="w-2 h-2 bg-globemallow-500 rounded-full mt-1" />}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      dismissNotification(notification);
                    }}
                    className="text-text-secondary hover:text-text-primary transition-colors"
                    aria-label="Dismiss notification"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-row justify-center border-t py-4">
          <Paragraph>No notifications</Paragraph>
        </div>
      )}
    </div>
  );
};
